/**
 * Which fields a view shows, and in what order.
 *
 * Every view type answers this the same way and then adds its own presentation
 * on top -- a grid adds a width and a frozen flag, a gallery adds a cover
 * column, a board adds the option each stack is keyed by. The part they share
 * is here, so the six of them cannot disagree about which fields a view holds
 * or what sequence they come in.
 *
 * Pure, so the answer can be tested without rendering anything.
 */
import type { Field, ViewOf } from '@/api/types'

/**
 * The two things every view type's field options say.
 *
 * Each type's own options extend this. Both are optional because a field the
 * view has not been told about yet has neither -- see `orderFields`.
 */
export interface FieldPlacement {
  hidden?: boolean
  order?: number
}

/**
 * Where a field with no stored order goes: last.
 *
 * The backend's own default, and it is 32767 rather than 0 for a reason it
 * states in the model -- a newly created field should be added to the *end* of
 * the view. Defaulting to zero here would put every field the view has not
 * been told about at the front, so a field somebody just made would appear
 * beside the primary one and then jump to the end when its options arrived.
 */
export const DEFAULT_FIELD_ORDER = 32767

/**
 * Every field this view holds, in the order it presents them.
 *
 * Three rules, in this order, and all three are the backend's rather than this
 * file's invention:
 *
 * 1. The primary field first, always. It identifies the row, so it is the one
 *    field that has to be there when everything else has scrolled away.
 * 2. Then by the field option's `order`.
 * 3. Then by field id -- the tie-break the API documents for `order`, without
 *    which two fields sharing an order would render in whatever sequence the
 *    response happened to arrive in, and swap places on the next refetch.
 *
 * Driven by the *fields*, not by the field options. A field whose options have
 * not arrived yet -- one just created, before the view has been told how to
 * show it -- appears with the defaults rather than vanishing. Upstream iterates
 * the options, so such a field is silently absent, which is far harder to
 * notice than one in the wrong place.
 */
export function orderFields(
  fields: readonly Field[],
  options: Record<string, FieldPlacement>,
): Field[] {
  const optionsFor = (field: Field): FieldPlacement => options[String(field.id)] ?? {}

  return [...fields].sort((a, b) => {
    if (a.primary !== b.primary) return a.primary ? -1 : 1
    const orderA = optionsFor(a).order ?? DEFAULT_FIELD_ORDER
    const orderB = optionsFor(b).order ?? DEFAULT_FIELD_ORDER
    return orderA - orderB || a.id - b.id
  })
}

/**
 * The fields this view shows, in the order it shows them.
 *
 * A field with no options is *shown*: the same rule `orderFields` follows, for
 * the same reason. Only an explicit `hidden: true` hides one.
 */
export function visibleFields(
  fields: readonly Field[],
  options: Record<string, FieldPlacement>,
): Field[] {
  return orderFields(fields, options).filter(
    (field) => options[String(field.id)]?.hidden !== true,
  )
}

/**
 * The fields this view does not show, in the order it would show them.
 *
 * The complement of `visibleFields`, and written beside it so the two cannot
 * drift apart: every field is in exactly one of the two lists, which is what
 * lets the row modal claim it is showing the whole row.
 *
 * Ordered the same way the shown ones are, even though nothing puts these side
 * by side with them. A hidden field has an `order` like any other, and sorting
 * by it is what makes the list stable -- the alternative is whatever sequence
 * the fields response happened to arrive in, which changes on a refetch.
 */
export function hiddenFields(
  fields: readonly Field[],
  options: Record<string, FieldPlacement>,
): Field[] {
  return orderFields(fields, options).filter(
    (field) => options[String(field.id)]?.hidden === true,
  )
}

/**
 * A view whose rows this build draws.
 *
 * Named here rather than spelled out at each call site: every menu that only
 * reads a view's filters, sortings or decorations works for all of them, and
 * writes through endpoints every view type shares. With the gantt it is every
 * view type there is bar the form, which draws no rows at all.
 */
export type RowsView = ViewOf<
  'grid' | 'gallery' | 'board' | 'list' | 'schedule' | 'gantt'
>
